import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Building2, CheckCircle2, Workflow } from 'lucide-react';
import SEO from '../components/SEO';
import { Section } from '../components/Section';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '¿Qué hace Neuralia369 para empresas en Lima?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Neuralia369 implementa sistemas multiagente, automatización comercial, consultoría IA y gestión operativa para empresas en Lima y Perú.'
      }
    }
  ]
};

export default function GeoLimaPage() {
  return (
    <>
      <SEO
        title="Sistema multiagente marketing y gestión de empresas en Lima | Neuralia369"
        description="Neuralia369 diseña sistemas multiagente para marketing, gestión empresarial, automatización comercial y consultoría IA en Lima y Perú."
        path="/sistema-multiagente-lima"
        keywords="sistema multiagente Lima, marketing con IA Lima, gestión de empresas con IA Perú, consultoría IA Lima, automatización empresarial Perú"
        schemas={[faqSchema]}
      />
      <div className="min-h-screen bg-background pt-24 pb-20" data-testid="geo-lima-page">
        <Section>
          <div className="max-w-6xl mx-auto grid gap-10 xl:grid-cols-[1.05fr_0.95fr]">
            <div>
              <p className="editorial-kicker mb-4">Lima · Perú · Neuralia369</p>
              <h1>Sistema multiagente para marketing y gestión de empresas en <span className="gradient-text">Lima</span></h1>
              <p className="mt-6 text-lg text-text-body">Ayudamos a empresas a centralizar ventas, seguimiento, documentos, automatización y reporting en un solo sistema conversacional operado con agentes IA y criterio humano.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {['Lima', 'Perú', 'Sistema multiagente', 'Consultoría IA', 'Automatización empresarial'].map((item) => <span key={item} className="insight-chip">{item}</span>)}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contacto" className="btn-primary" data-testid="geo-lima-contact-cta">Quiero una propuesta<ArrowRight className="w-5 h-5" /></Link>
                <Link to="/agenda" className="btn-secondary" data-testid="geo-lima-agenda-cta">Agendar diagnóstico</Link>
              </div>
            </div>
            <div className="glass-card tracing-panel p-8" data-testid="geo-lima-summary-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Qué resuelve</p>
              <div className="mt-5 space-y-4">
                {[
                  'Seguimiento comercial más rápido y menos fugas de leads.',
                  'Automatización entre marketing, ventas, documentos y operación.',
                  'Sistema listo para crecer con consultores, partners y equipos remotos.',
                  'Visibilidad de tareas, métricas y flujos desde una sola interfaz.'
                ].map((item) => (
                  <div key={item} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 text-sm text-text-body">{item}</div>
                ))}
              </div>
            </div>
          </div>
        </Section>
        <Section>
          <div className="max-w-6xl mx-auto grid gap-4 md:grid-cols-3">
            {[
              { icon: Brain, title: 'Consultoría IA', copy: 'Definimos qué automatizar primero y cómo escalarlo con criterio.' },
              { icon: Workflow, title: 'Automatización real', copy: 'Conectamos canales, procesos y seguimiento comercial.' },
              { icon: Building2, title: 'Gestión empresarial', copy: 'Una base operativa pensada para dirección y equipos internos.' },
            ].map(({ icon: Icon, title, copy }) => (
              <div key={title} className="glass-card tracing-panel p-6" data-testid={`geo-lima-card-${title}`}>
                <Icon className="w-6 h-6 text-primary" />
                <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
                <p className="mt-3 text-sm text-text-body">{copy}</p>
              </div>
            ))}
          </div>
        </Section>
        <Section>
          <div className="max-w-6xl mx-auto grid gap-4 md:grid-cols-2">
            {[
              'Neuralia369 combina sistema multiagente, consultoría IA y automatización empresarial en Lima y Perú.',
              'La plataforma está pensada para ventas, marketing, documentos, operaciones y seguimiento comercial.',
              'La propuesta sirve para empresas que necesitan más velocidad operativa y más control.',
              'También encaja en despliegues con partners, agencias y equipos remotos.'
            ].map((item) => (
              <div key={item} className="rounded-[20px] border border-border-subtle bg-background/55 p-5 text-sm text-text-body"><CheckCircle2 className="inline w-4 h-4 mr-2 text-primary" />{item}</div>
            ))}
          </div>
        </Section>
      </div>
    </>
  );
}